import {
  animate,
  ImageScaleView,
  ImageScaleViewOpts,
  Transition,
  View,
} from '@play-co/timestep-core/ui';
import { animDuration } from 'src/lib/utils';
import { GenericProgressBar } from './GenericProgressBar';

export class ProgressBar extends GenericProgressBar {
  /**
   * Set progress based on the domain.
   */
  public set progress(value: number) {
    this.progressFill.style.width = this.toWidth(value);
    this._progress = value;
  }

  private progressFill: ImageScaleView;

  constructor(
    opts: {
      superview: View;
      bgStyle?: Omit<ImageScaleViewOpts, 'superview'>;
      fillStyle?: Omit<ImageScaleViewOpts, 'superview'>;
    } & {
      domain?: number[];
      progress?: number;
    },
  ) {
    super({
      superview: opts.superview,
      image: 'assets/ui/shared/lossless/gauge_bg.png',
      width: 500,
      height: 88,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 20, right: 20 },
        vertical: { top: 20, bottom: 20 },
      },
      ...(opts.bgStyle || {}),
    });

    this.progressFill = new ImageScaleView({
      superview: this,
      image: 'assets/ui/shared/lossless/gauge_fill.png',
      x: 4,
      y: 3,
      width: 0,
      height: 78,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 20, right: 20 },
        vertical: { top: 20, bottom: 20 },
      },
      ...(opts.fillStyle || {}),
    });

    this._progress = opts.progress ?? 0;
    this.domain = opts.domain ?? [0, 1];
  }

  /**
   * Aniamte current progress to the given value.
   * @param value - Target value.
   * @param duration - Animation duration.
   */
  public async animateProgress(
    value: number,
    duration: number = animDuration,
    easing: Transition = animate.easeInOut,
  ) {
    this._progress = value;
    await animate(this.progressFill)
      .then({ width: this.toWidth(value) }, duration, easing)
      .toPromise();
  }

  /**
   * Convert a value to width considering the domain.
   * @param value - Target value.
   * @returns
   */
  private toWidth(value: number) {
    // Normalize value.
    const normalizedProgress = this.getNormalizedProgress(value);

    // Convert to width domain.
    const [minWidth, maxWidth] = [30, 497];
    return normalizedProgress
      ? Math.max(minWidth, normalizedProgress * maxWidth)
      : 0;
  }
}
